import * as React from 'react';

/**
 * Creates a context for shared state from an existing hook.  Takes a standard hook
 * and it returns a new hook and context provider that can be used repeatedly in multiple
 * components while sharing the same state.  Does not support a hook with a default
 * value or other arguments.
 *
 * @param  {function} useHook   This is the hook that will be shared
 *
 * @return {array}              first value is the new hook with shared state
 *                              second value is the provider
 */

type ReturnType<T> = [() => T, React.ComponentType];

export function createSharedStateFromHook<T>(useHook: () => T): ReturnType<T> {
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  const Context = React.createContext<T>(null);

  function UseHookProvider(props?: { children?: React.ReactNode }) {
    return <Context.Provider value={useHook()} {...props} />;
  }

  const useHookShared = () => {
    const context = React.useContext(Context);
    if (!context)
      throw new Error(
        `The ${useHook.name} hook should be used within a Provider`,
      );

    return context;
  };

  return [useHookShared, UseHookProvider];
}
